import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSessions,
  fetchBlocks,
  setCurrentPage,
  addSession,
  updateSession,
  selectBlocks
} from '../../redux/session/sessionSlice';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';

const Sessions = () => {
  const dispatch = useDispatch();
  const { sessions, totalPages, currentPage } = useSelector(state => state.sessions);
  const blocks = useSelector(selectBlocks);

  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [formData, setFormData] = useState({
    topic: '',
    notes: '',
    session_date: '',
    start_time: '',
    end_time: '',
    block_venue_id: '',
  });

  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const sessionsPerPage = 5;
  const maxPageNumbers = 5;

  useEffect(() => {
    dispatch(fetchSessions({ search: '', page: currentPage, limit: sessionsPerPage }));
    dispatch(fetchBlocks());
  }, [dispatch, currentPage]);

  const handleModalOpen = (session = null) => {
    if (session) {
      setEditingSession(session);
      setFormData({
        topic: session.topic,
        notes: session.notes,
        session_date: session.session_date,
        start_time: session.start_time,
        end_time: session.end_time,
        block_venue_id: session.block_venue_id,
      });
    } else {
      setEditingSession(null);
      setFormData({
        topic: '',
        notes: '',
        session_date: '',
        start_time: '',
        end_time: '',
        block_venue_id: '',
      });
    }
    setShowModal(true);
  };

  const handleModalClose = () => setShowModal(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSession) {
        await dispatch(updateSession({ id: editingSession.id, sessionData: formData }));
        toastr.success('Session updated successfully!');
      } else {
        await dispatch(addSession(formData));
        toastr.success('Session added successfully!');
      }
      setFormData({ topic: '', notes: '', session_date: '', start_time: '', end_time: '', block_venue_id: '' });
      setEditingSession(null);
      setShowModal(false);
      dispatch(fetchSessions({ search: '', page: currentPage, limit: sessionsPerPage }));
    } catch {
      toastr.error('Error saving session!');
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hour, minute] = time.split(':');
    const h = parseInt(hour);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minute} ${ampm}`;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) dispatch(setCurrentPage(pageNumber));
  };

  const pageNumbers = [];
  const startPage = Math.max(1, currentPage - Math.floor(maxPageNumbers / 2));
  const endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);
  for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

  const handleViewSummary = async (sessionId) => {
    setLoadingSummary(true);
    setShowSummaryModal(true);
    try {
      const res = await axios.get(`https://aadhavacademy.com/api/session/session_summary.php?id=${sessionId}`);
      setSummaryData(res.data);
    } catch {
      toastr.error('Failed to load session summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  const formatAttendance = (present, total) => {
    const presentNum = Number(present);
    const totalNum = Number(total);
    return totalNum === 0 ? "-" : `${presentNum} / ${totalNum}`;
  };

  return (
    <section className="content py-3">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
          <h3 className="mb-2">Sessions List</h3>
          <button className="btn btn-sm btn-success" onClick={() => handleModalOpen()}>
            <i className="fas fa-plus"></i> Add Session
          </button>
        </div>

        <div className="card shadow-sm">
          <div className="card-body table-responsive p-0">
            <table className="table table-sm table-bordered table-hover mb-0 text-sm">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '45%' }}>Topic & Notes</th>
                  <th style={{ width: '20%' }}>Date & Time</th>
                  <th style={{ width: '20%' }}>Block & Venue</th>
                  <th className="text-center" style={{ width: '15%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-3">No sessions found</td></tr>
                ) : (
                  sessions.map(sess => (
                    <tr key={sess.id}>
                      <td><strong>{sess.topic}</strong><br />{sess.notes}</td>
                      <td>{sess.session_date} | {formatTime(sess.start_time)} - {formatTime(sess.end_time)}</td>
                      <td>{sess.block_name} - {sess.venue_name}</td>
                      <td className="text-center">
                        <button className="btn btn-sm btn-primary me-2" onClick={() => handleViewSummary(sess.id)}>
                          <i className="fas fa-eye"></i>
                        </button>
                        <button className="btn btn-sm btn-info" onClick={() => handleModalOpen(sess)}>
                          <i className="fas fa-edit"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-2 d-flex justify-content-end flex-wrap">
          <ul className="pagination pagination-sm m-0 flex-wrap">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => paginate(1)}>First</button>
            </li>
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => paginate(currentPage - 1)}>Previous</button>
            </li>
            {pageNumbers.map(num => (
              <li key={num} className={`page-item ${currentPage === num ? 'active' : ''}`}>
                <button className="page-link" onClick={() => paginate(num)}>{num}</button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => paginate(currentPage + 1)}>Next</button>
            </li>
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => paginate(totalPages)}>Last</button>
            </li>
          </ul>
        </div>

        {showModal && (
          <div className="modal d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content shadow">
                <div className="modal-header bg-light">
                  <h5 className="modal-title">{editingSession ? 'Edit Session' : 'Add New Session'}</h5>
                  <button type="button" className="btn-close" onClick={handleModalClose}></button>
                </div>
                <form onSubmit={handleFormSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Topic</label>
                      <input type="text" name="topic" value={formData.topic} onChange={handleFormChange} className="form-control" required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Notes</label>
                      <textarea name="notes" value={formData.notes} onChange={handleFormChange} className="form-control" />
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Date</label>
                        <input type="date" name="session_date" value={formData.session_date} onChange={handleFormChange} className="form-control" required />
                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label">Start Time</label>
                        <input type="time" name="start_time" value={formData.start_time} onChange={handleFormChange} className="form-control" required />
                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label">End Time</label>
                        <input type="time" name="end_time" value={formData.end_time} onChange={handleFormChange} className="form-control" required />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Block & Venue</label>
                      <select name="block_venue_id" value={formData.block_venue_id} onChange={handleFormChange} className="form-control" required>
                        <option value="">Select Block</option>
                        {blocks.map(b => (
                          <option key={b.id} value={b.id}>{b.block_name} - {b.venue_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={handleModalClose}>Cancel</button>
                    <button type="submit" className="btn btn-success">{editingSession ? 'Update' : 'Save'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {showSummaryModal && (
          <div className="modal d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content shadow">
                <div className="modal-header bg-light">
                  <h5 className="modal-title">Session Summary</h5>
                  <button type="button" className="btn-close" onClick={() => setShowSummaryModal(false)}></button>
                </div>
                <div className="modal-body">
                  {loadingSummary ? (
                    <p className="text-center mb-0">Loading...</p>
                  ) : summaryData ? (
                    <>
                      <h6><strong>Topic: {summaryData.session.topic}</strong><br />{summaryData.session.notes}</h6>
                      <p><strong>Date:</strong> {formatDate(summaryData.session.session_date)} {formatTime(summaryData.session.start_time)} - {formatTime(summaryData.session.end_time)}</p>
                      <p><strong>Block & Venue:</strong> {summaryData.session.block_name} - {summaryData.session.venue_name}</p>
                      <table className="table table-sm table-bordered">
                        <caption className="caption-top text-lg font-semibold text-gray-800 mb-2">Overall Attendance Today</caption>
                        <thead className="table-light">
                          <tr><th>Boys</th><th>Girls</th><th>Total</th></tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>18 / 43</td>
                            <td>16 / 25</td>
                            <td>34 / 68</td>
                          </tr>
                        </tbody>
                      </table>
                      <h6 className="mt-3">📊 School-wise Summary</h6>
                      <table className="table table-sm table-bordered">
                        <thead className="table-light"><tr><th>School</th><th>Boys</th><th>Girls</th><th>Total</th></tr></thead>
                        <tbody>
                          {summaryData.schoolStats.map((s, idx) => (
                            <tr key={idx}>
                              <td>{s.school}</td>
                              <td>{s.boys_present} / {s.boys_total}</td>
                              <td>{formatAttendance(s.girls_present, s.girls_total)}</td>
                              <td>{s.total_present} / {s.total_students}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <h6 className="mt-3">👥 Student Attendance</h6>
                      <table className="table table-sm table-bordered">
                        <thead className="table-light"><tr><th>Student</th><th>Status</th></tr></thead>
                        <tbody>
                          {summaryData.students.map((st, idx) => (
                            <tr key={idx}><td>{st.name}</td><td>{st.status ? '✅ Present' : '❌ Absent'}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <p className="text-center text-danger">No data available.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Sessions;
